import { useMemo, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/hooks';
import {
  selectManagers,
  selectProfessions,
  selectSelectedEquipmentTypes,
  selectSelectedManagerIds,
  selectSelectedProfessionIds,
} from '@store/selectors/monitor.selectors';
import { getEquipmentIconFromType } from '@utils/equipmentIcons';
import { nameInitials } from '@utils/nameInitials';
import { professionChipStyle, professionColor } from '@utils/professionColor';
import { tradeLabel } from '@utils/tradeLabel';
import { TradePill } from '@components/ui/TradePill/TradePill';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { ClearFiltersButton } from '@components/ui/ClearFiltersButton/ClearFiltersButton';
import {
  useClearEquipmentFilters,
  useClearManagerFilters,
  useClearProfessionFilters,
  useToggleEquipmentFilter,
  useToggleManagerFilter,
  useToggleProfessionFilter,
} from '../hooks';
import { ExpandableSection } from './ExpandableSection';
import { useWorkforceSource } from './useRailSource';
import styles from './leftRail.module.css';

/**
 * LeftRail "Workforce" tab — three collapsible sections stacked
 * vertically: Professions, Managers, Equipment. Mirrors
 * trusstor_dashboard's `WorkForceTab.tsx`. Manager and equipment lists
 * are intentionally rendered separately, NOT folded into the professions
 * list, so each section can collapse independently.
 *
 * Each row is a toggle button: clicking anywhere on the row flips its
 * checkbox, which dispatches a filter toggle into the monitor slice.
 * Filtered ids cascade through the selectors so the building chips, floor
 * popover, drawer, and special-area cards all narrow together.
 */
export function WorkforceTab() {
  const { t } = useTranslation();
  const toggleProfession = useToggleProfessionFilter();
  const toggleManager = useToggleManagerFilter();
  const toggleEquipment = useToggleEquipmentFilter();
  const clearProfessions = useClearProfessionFilters();
  const clearManagers = useClearManagerFilters();
  const clearEquipment = useClearEquipmentFilters();
  // On site now, or at the replayed minute.
  const { professionRows: onSiteProfessionRows, managerRows: onSiteManagerRows, equipmentRows } = useWorkforceSource();
  const selectedProfIds = useAppSelector(selectSelectedProfessionIds);
  const selectedManagerIds = useAppSelector(selectSelectedManagerIds);
  const selectedEquipmentTypes = useAppSelector(selectSelectedEquipmentTypes);
  const allProfessions = useAppSelector(selectProfessions);
  const allManagers = useAppSelector(selectManagers);

  const profSet = useMemo(() => new Set(selectedProfIds), [selectedProfIds]);
  const managerSet = useMemo(() => new Set(selectedManagerIds), [selectedManagerIds]);
  const equipmentSet = useMemo(() => new Set(selectedEquipmentTypes), [selectedEquipmentTypes]);

  // When a filtered profession has zero active workers (or a filtered
  // manager goes offline), the upstream-driven selectors drop their row
  // and the user loses the ability to *individually* untoggle that
  // filter — only "Clear all" would work. Append a synthetic row for
  // each selected id missing from the live list, sourced from the full
  // catalog. The row renders with `active: 0`, gets the same checked
  // styling, and disappears once the user toggles it off.
  const professionRows = useMemo(() => {
    const live = onSiteProfessionRows.map((row) => ({ ...row, offSite: false }));
    const liveIds = new Set(live.map((r) => r.id));
    const missing = selectedProfIds.filter((id) => !liveIds.has(id));
    if (missing.length === 0) return live;
    const profById = new Map(allProfessions.map((p) => [p._id ?? '', p]));
    const ghost = missing.flatMap((id) => {
      const p = profById.get(id);
      if (!p) return [];
      return [{
        id,
        specialty: p.specialty,
        tradeId: p.tradeId,
        tradeGroup: p.tradeGroup,
        tradeColor: professionColor(p.backgroundColor),
        contractor: p.contractor,
        active: 0,
        onSite: 0,
        manager: '',
        floors: [] as string[],
        offSite: true,
      }];
    });
    return [...live, ...ghost];
  }, [onSiteProfessionRows, selectedProfIds, allProfessions]);

  const managerRows = useMemo(() => {
    const live = onSiteManagerRows.map((row) => ({ ...row, offSite: false }));
    const liveIds = new Set(live.map((r) => r.id));
    const missing = selectedManagerIds.filter((id) => !liveIds.has(id));
    if (missing.length === 0) return live;
    const ghost = missing.flatMap((id) => {
      const m = allManagers.find((mm) => {
        const mr = mm as { _id?: string; name?: string };
        return (mr._id ?? mr.name) === id;
      }) as { _id?: string; name?: string; tradeGroup?: string; backgroundColor?: string } | undefined;
      if (!m?.name) return [];
      return [{
        id,
        name: m.name,
        role: m.tradeGroup ?? '',
        backgroundColor: professionColor(m.backgroundColor),
        offSite: true,
      }];
    });
    return [...live, ...ghost];
  }, [onSiteManagerRows, selectedManagerIds, allManagers]);

  function handleRowKey(e: KeyboardEvent<HTMLDivElement>, fire: () => void) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fire();
    }
  }

  return (
    <div className={styles.sections}>
      <ExpandableSection
        title={t('monitor.workforce.sections.professions')}
        count={professionRows.length}
        defaultOpen
        emptyText={t('monitor.workforce.empty')}
        actions={<ClearFiltersButton count={selectedProfIds.length} onClear={clearProfessions} />}
      >
        {professionRows.map((r) => {
          // Mirrors trusstor's ProfessionDisplayWithTradeIcon chip-text:
          // specialty when present, else translated tradeId (humanised
          // when no explicit translation exists), else tradeGroup.
          const label =
            r.specialty ?? tradeLabel(t, r.tradeId) ?? r.tradeGroup ?? 'Unknown';
          const checked = profSet.has(r.id);
          const fire = () => toggleProfession(r.id);
          return (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              aria-pressed={checked}
              aria-label={t('monitor.workforce.filterToggle', { name: label })}
              onClick={fire}
              onKeyDown={(e) => handleRowKey(e, fire)}
              className={`${styles.professionRow} ${styles.rowToggle}${
                checked ? ` ${styles.rowSelected}` : ''
              }${r.offSite ? ` ${styles.rowOffSite}` : ''}`}
            >
              <Checkbox checked={checked} ariaLabel="" presentational />
              <TradePill
                label={label}
                backgroundColor={r.tradeColor}
                {...(r.tradeId ? { tradeId: r.tradeId } : {})}
              />
              {r.contractor && (
                <span className={styles.contractorText}>{r.contractor}</span>
              )}
              {r.offSite ? (
                <span className={styles.offSiteTag}>{t('monitor.workforce.offSite')}</span>
              ) : (
                <span className={styles.professionRowCount}>{r.active}</span>
              )}
            </div>
          );
        })}
      </ExpandableSection>

      {/* Mirrors trusstor's `hideEmptyList` on ManagersFilterSection /
          EquipmentTypeFilterSection — when nothing is currently on site,
          the whole section disappears (header included) rather than
          rendering "Managers (0)" over an empty body. Professions keeps
          its empty-state copy since it's the section that always renders. */}
      {managerRows.length > 0 && (
        <ExpandableSection
          title={t('monitor.workforce.sections.managers')}
          count={managerRows.length}
          defaultOpen
          actions={<ClearFiltersButton count={selectedManagerIds.length} onClear={clearManagers} />}
        >
          {managerRows.map((m) => {
            const checked = managerSet.has(m.id);
            const fire = () => toggleManager(m.id);
            return (
              <div
                key={m.id}
                role="button"
                tabIndex={0}
                aria-pressed={checked}
                aria-label={t('monitor.workforce.filterToggle', { name: m.name })}
                onClick={fire}
                onKeyDown={(e) => handleRowKey(e, fire)}
                className={`${styles.managerRow} ${styles.rowToggle}${
                  checked ? ` ${styles.rowSelected}` : ''
                }${m.offSite ? ` ${styles.rowOffSite}` : ''}`}
              >
                <Checkbox checked={checked} ariaLabel="" presentational />
                <span
                  className={styles.managerRowAvatar}
                  style={professionChipStyle(m.backgroundColor)}
                >
                  {nameInitials(m.name)}
                </span>
                <span className={styles.managerRowName}>{m.name}</span>
                {m.role && <span className={styles.managerRowRole}>{m.role}</span>}
                {m.offSite && (
                  <span className={styles.offSiteTag}>{t('monitor.workforce.offSite')}</span>
                )}
              </div>
            );
          })}
        </ExpandableSection>
      )}

      {equipmentRows.length > 0 && (
        <ExpandableSection
          title={t('monitor.workforce.sections.equipment')}
          count={equipmentRows.length}
          defaultOpen
          actions={<ClearFiltersButton count={selectedEquipmentTypes.length} onClear={clearEquipment} />}
        >
          {equipmentRows.map((e) => {
            const checked = equipmentSet.has(e.type);
            const fire = () => toggleEquipment(e.type);
            return (
              <div
                key={e.type}
                role="button"
                tabIndex={0}
                aria-pressed={checked}
                aria-label={t('monitor.workforce.filterToggle', { name: e.label })}
                onClick={fire}
                onKeyDown={(ev) => handleRowKey(ev, fire)}
                className={`${styles.equipmentRow} ${styles.rowToggle}${
                  checked ? ` ${styles.rowSelected}` : ''
                }`}
              >
                <Checkbox checked={checked} ariaLabel="" presentational />
                <img
                  className={styles.equipmentRowIcon}
                  src={getEquipmentIconFromType(e.type)}
                  alt=""
                />
                <span className={styles.equipmentRowName}>{e.label}</span>
                <span className={styles.equipmentRowCount}>
                  {e.activeCount}
                  {e.totalCount > 0 && (
                    <span className={styles.equipmentRowCountTotal}>/{e.totalCount}</span>
                  )}
                </span>
              </div>
            );
          })}
        </ExpandableSection>
      )}
    </div>
  );
}
